import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import anime from 'animejs';

import { getViewportRange } from '../../tools/viewport';
import { Link } from '../Link';
import { Text } from '../Text';
import { Secuence } from '../Secuence';
import { SCHEME_NORMAL, SCHEME_EXPAND } from './Menu.constants';

const NAV_GROUPS = [
  [
    { href: '/blog', label: 'Blog' },
    { href: '/certificates', label: 'Certificates' },
    { href: '/projects', label: 'Projects' }
  ],
  [
    { href: '/about', label: 'About' },
    { href: '/resume', label: 'Resume' }
  ]
];

class Component extends React.PureComponent {
  static displayName = 'Menu';

  static propTypes = {
    theme: PropTypes.object.isRequired,
    classes: PropTypes.object.isRequired,
    energy: PropTypes.object.isRequired,
    audio: PropTypes.object.isRequired,
    sounds: PropTypes.object.isRequired,
    className: PropTypes.any,
    scheme: PropTypes.oneOf([SCHEME_NORMAL, SCHEME_EXPAND]),
    onEnter: PropTypes.func,
    onExit: PropTypes.func,
    onLinkStart: PropTypes.func,
    onLinkEnd: PropTypes.func
  };

  static defaultProps = {
    scheme: SCHEME_NORMAL
  };

  constructor () {
    super(...arguments);

    this.state = {
      showSecuence: false
    };
  }

  componentDidMount () {
    window.addEventListener('route-change', this.onURLChange);
  }

  componentDidUpdate (prevProps) {
    const { energy } = this.props;

    if (prevProps.energy.status !== energy.status) {
      if (energy.entering) {
        this.setState({ showSecuence: true }); // eslint-disable-line react/no-did-update-set-state
      } else if (energy.exiting) {
        this.setState({ showSecuence: false }); // eslint-disable-line react/no-did-update-set-state
      }
    }
  }

  componentWillUnmount () {
    const elements = this.element.querySelectorAll('a, b');
    anime.remove(elements);

    window.removeEventListener('route-change', this.onURLChange);
  }

  onURLChange = () => {
    this.forceUpdate();
  }

  isItemActive = (href) => {
    if (typeof window === 'undefined') return false;
    const current = window.location.pathname;
    if (href === '/blog' && current.startsWith('/blog')) return true;
    if ((href === '/projects' || href === '/projelerim') && (current.startsWith('/projects') || current.startsWith('/projelerim'))) return true;
    if ((href === '/certificates' || href === '/sertifikalarim') && (current.startsWith('/certificates') || current.startsWith('/sertifikalarim'))) return true;
    if ((href === '/about' || href === '/hakkimda') && (current.startsWith('/about') || current.startsWith('/hakkimda'))) return true;
    if (href === '/resume' && current.startsWith('/resume')) return true;
    return false;
  }

  enter () {
    const { scheme } = this.props;

    if (scheme === SCHEME_NORMAL) {
      this.animateNormalEnter();
    } else {
      this.animateExpandEnter();
    }
  }

  animateNormalEnter () {
    const { energy, onEnter } = this.props;
    const { duration } = energy;

    const divisors = this.element.querySelectorAll('b');
    const links = this.element.querySelectorAll('a');

    anime.set(links, { opacity: 1 });

    anime({
      targets: divisors,
      easing: 'easeOutCubic',
      scaleY: [0, 1],
      duration: duration.enter,
      delay: (divisor, index) => index * duration.stagger,
      complete: () => onEnter && onEnter()
    });
  }

  animateExpandEnter () {
    const { energy, sounds, onEnter } = this.props;
    const { duration } = energy;
    const viewportRange = getViewportRange();

    const divisors = this.element.querySelectorAll('b');
    const links = this.element.querySelectorAll('a');

    sounds.expand.play();

    anime({
      targets: divisors,
      easing: 'easeOutCubic',
      scaleY: [0, 1],
      delay: duration.enter / 2,
      duration: duration.enter / 2
    });

    anime({
      targets: links,
      easing: 'easeOutCubic',
      opacity: 1,
      translateX: (link, index) => {
        const center = (links.length - 1) / 2;
        return [(center - index) * 75, 0];
      },
      delay: viewportRange.small ? 0 : duration.enter / 2,
      duration: viewportRange.small ? duration.enter : duration.enter / 2,
      complete: () => onEnter && onEnter()
    });
  }

  exit () {
    const { energy, onExit } = this.props;
    const { duration } = energy;

    const divisors = this.element.querySelectorAll('b');
    const links = this.element.querySelectorAll('a');

    anime({
      targets: divisors,
      easing: 'easeOutCubic',
      scaleY: [1, 0],
      duration: duration.exit
    });
    anime({
      targets: links,
      easing: 'easeOutCubic',
      opacity: 0,
      duration: duration.exit,
      complete: () => onExit && onExit()
    });
  }

  render () {
    const {
      theme,
      classes,
      energy,
      audio,
      sounds,
      className,
      scheme,
      onEnter,
      onExit,
      onLinkStart,
      onLinkEnd,
      ...etc
    } = this.props;
    const { showSecuence } = this.state;

    const animateText = scheme === SCHEME_NORMAL;
    const linkProps = {
      className: cx(classes.item, classes.link),
      onMouseEnter: () => sounds.hover.play(),
      onLinkStart,
      onLinkEnd
    };

    return (
      <Secuence
        animation={{ show: showSecuence, independent: true }}
        stagger
      >
        <nav
          className={cx(classes.root, className)}
          ref={ref => (this.element = ref)}
          {...etc}
        >
          {NAV_GROUPS.map((group, groupIndex) => (
            <React.Fragment key={groupIndex}>
              {groupIndex > 0 && (
                <b className={cx(classes.item, classes.divisor, classes.groupDivisor)}>|</b>
              )}
              <div className={classes.group}>
                {group.map((item, itemIndex) => (
                  <React.Fragment key={item.href}>
                    {itemIndex > 0 && (
                      <b className={cx(classes.item, classes.divisor)}>|</b>
                    )}
                    <Link
                      href={item.href}
                      {...linkProps}
                      className={cx(
                        linkProps.className,
                        this.isItemActive(item.href) && 'link-active'
                      )}
                    >
                      <Text
                        animation={{ animate: animateText }}
                        audio={{ silent: !animateText }}
                      >
                        {item.label}
                      </Text>
                    </Link>
                  </React.Fragment>
                ))}
              </div>
            </React.Fragment>
          ))}
        </nav>
      </Secuence>
    );
  }
}

export { Component };

// Commit trace: 2025-08-04T17:51:41 - perf: speed up execution in Menu.js

// Commit trace: 2025-08-13T12:56:10 - refactor: clean up code structure in Menu.js

// Commit trace: 2026-04-16T11:12:12 - feat: enhance functionality of Menu.js
